import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SltApiResponseService } from '../../services/slt-api-response.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-slt-api-response',
    templateUrl: './slt-api-response.component.html',
    styleUrls: ['./slt-api-response.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltApiResponseComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'SLT API Response';
  minimise = false;
  loading = false;

  // Component-specific properties
  sltApiResponseData: any[] = [];
  jsonView: string = '';
  meCodeList: any[] = [];

  // Global settings
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private sltApiResponseService: SltApiResponseService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Load ME codes list and SLT response data
        this.getMeCodesList();
      }
    } catch (e) {
      console.error('Error in SltApiResponse init:', e);
      this.loading = false;
    }
  }

  /**
   * Get ME Codes List and then fetch SLT Response Data
   */
  getMeCodesList() {
    this.loading = true;
    this.cdr.detectChanges();

    this.sltApiResponseService.getMeCodesList(
      this.GENERAL_CONFIG.base_url,
      this.rowData.TAX_YEAR
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === "1") {
          this.meCodeList = response.jsonObject || [];
          // After getting ME codes, fetch SLT response data
          this.getSltResponseData();
        } else {
          this.loading = false;
          this._snackBar.open(response.message || 'Error loading ME codes', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading ME codes:', error);
        this._snackBar.open('Error loading ME codes. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Get SLT Response Data from API
   */
  getSltResponseData() {
    this.sltApiResponseService.getSltResponseData(
      this.GENERAL_CONFIG.base_url,
      'STATE_API_RESPONSE',
      this.rowData.LOG_ID
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === "1") {
          try {
            // Parse JSON view data
            const groupSyncMsg = response.jsonObject[0].GROUP_SYNC_MSG;
            this.jsonView = this.formatJsonView(safeJsonInput<any>(groupSyncMsg, null, 'slt-api-response.parse1'));

            // Parse response data
            const responseData = safeJsonInput<any>(response.jsonObject[0].RESPONSE, null, 'slt-api-response.parse2');
            const entities = responseData.entities || [];

            // Check each entity's ME code against meCodeList
            entities.forEach((row: any) => {
              row.isPresent = this.meCodeExists(row.me_code);
            });

            this.sltApiResponseData = entities;
            this.loading = false;
            this.cdr.detectChanges();
          } catch (parseError) {
            console.error('Error parsing response data:', parseError);
            this.loading = false;
            this._snackBar.open('Error parsing response data', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.cdr.detectChanges();
          }
        } else {
          this.loading = false;
          this._snackBar.open(response.message || 'Error loading SLT response', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading SLT response:', error);
        this._snackBar.open('Error loading SLT response. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Check if ME code exists in meCodeList
   * @param meCode ME code to check
   * @returns true if exists, false otherwise
   */
  meCodeExists(meCode: any): boolean {
    return this.meCodeList.some((el: any) => el.ME_CODE === meCode);
  }

  /**
   * Format JSON object to HTML string for display
   * @param obj JSON object to format
   * @returns HTML formatted string
   */
  formatJsonView(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
